<script setup lang="ts">
import { toRefs, inject, computed } from "vue";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useI18n } from "vue-i18n";
import { useSegmentBuilderStore } from "@/modules/segments/store";
const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;
const { t } = useI18n();
import { formatNumber } from "chartjs-graph";
import { activeFiltersDisplay } from "@/Utils/FilterUtils";
import useDialog from "@/ui/composables/useDialog";

const editFilter = inject("editFilter");
const emit = defineEmits(["remove-filter"]);

const props = defineProps<{
	usedFilter: ActiveFilterDTO;
	partType: "include" | "exclude";
	filterIdx: Number;
	partIdx: Number;
}>();

const { usedFilter, partType, filterIdx, partIdx } = toRefs(props);

const showFilterBarToEditFilter = () => {
	editFilter(usedFilter.value, partType.value);
};

const removeFilterFromPart = async () => {
	const confirm = await useDialog().confirm({
		title: "Delete Filter Condition",
		message: t("segments.deleteFilter"),
		isDelete: true,
	});

	if (!confirm) {
		return;
	}

	emit("remove-filter");
};

const getIntrospectCountForFilter = computed(() => {
	const introspectResult = segmentBuilderState.introspectResult;

	let index = null;
	for (let i = 0; i < introspectResult.length; i++) {
		if (
			introspectResult[i]?.part_idx === partIdx.value &&
			introspectResult[i]?.filter_idx === filterIdx.value
		) {
			index = i;
		}
	}

	if (index === null) {
		return {
			previousFilterCount: null,
			currentFilterCount: null,
			relativeFilterImpact: 0,
		};
	}

	const previousFilterCount =
		introspectResult[index - 1]?.count ?? segmentBuilderState.totalSegmentBase;
	const currentFilterCount = introspectResult[index]?.count ?? 0;
	const relativeFilterImpact = (currentFilterCount / previousFilterCount) * 100;

	return {
		previousFilterCount: formatNumber(previousFilterCount, "default"),
		currentFilterCount: formatNumber(currentFilterCount, "default"),
		relativeFilterImpact: formatNumber(relativeFilterImpact, "percent"),
	};
});
</script>

<template>
	<v-card class="mx-auto">
		<v-card-actions>
			<div class="d-flex cursor-pointer" @click="showFilterBarToEditFilter()">
				<div style="padding-top: 1px">
					{{ t(`properties.${usedFilter.dimension.name_clean}.title`) }}
				</div>
				<div class="text-caption px-2 pt-1">
					is {{ activeFiltersDisplay(usedFilter) }}
				</div>
			</div>
			<v-spacer />
			<div class="pr-10">
				<v-sheet
					class="d-flex align-center mx-auto d-flex"
					color="background"
					width="300"
					rounded="lg"
				>
					<v-progress-linear
						v-model="getIntrospectCountForFilter.relativeFilterImpact"
						:color="partType === 'include' ? 'primary' : 'active-secondary'"
						height="16"
						:indeterminate="
							getIntrospectCountForFilter.currentFilterCount === null &&
							!segmentBuilderState.introspectErrorDetected
						"
					>
						<template v-slot:default="{ value }">
							<span class="text-caption">
								<span
									v-if="
										getIntrospectCountForFilter.currentFilterCount === null &&
										!segmentBuilderState.introspectErrorDetected
									"
									>Calculating ..</span
								>
								<strong v-else>{{ Math.ceil(value) }}%</strong>
							</span>
						</template>
					</v-progress-linear>
					<div class="px-3 text-caption color-on-surface">
						<strong>{{
							getIntrospectCountForFilter.currentFilterCount ?? ".."
						}}</strong
						><span
							>/{{
								getIntrospectCountForFilter.previousFilterCount ?? ".."
							}}</span
						>
					</div>
				</v-sheet>
			</div>

			<v-btn
				:color="partType === 'include' ? 'primary' : 'active-secondary'"
				icon="mdi-trash-can-outline"
				@click="removeFilterFromPart"
			></v-btn>

			<!--			<v-btn-->
			<!--				:icon="showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down'"-->
			<!--				@click="showDetails = !showDetails"-->
			<!--			></v-btn>-->
		</v-card-actions>
		<!--		<v-expand-transition>-->
		<!--			<div v-show="showDetails">-->
		<!--				<v-divider></v-divider>-->

		<!--				<v-card-text>-->
		<!--					&lt;!&ndash;		todo nice interface for listing the different filter configurations (normal, hierarchy bucket, period etc.)			&ndash;&gt;-->
		<!--					&lt;!&ndash;		show include / exclude values			&ndash;&gt;-->
		<!--					<pre>{{ getIntrospectCountForFilter }}</pre>-->
		<!--					<pre>{{ usedFilter }}</pre>-->
		<!--					<v-btn-->
		<!--						@click="removeFilterFromPart"-->
		<!--						variant="tonal"-->
		<!--						density="compact"-->
		<!--						color="error"-->
		<!--						>Remove This Filter</v-btn-->
		<!--					>-->
		<!--				</v-card-text>-->
		<!--			</div>-->
		<!--		</v-expand-transition>-->
	</v-card>
	<div class="d-flex py-2">
		<div class="w-100"></div>
		<div class="w-100 pt-3 dividerMaxWidth"><v-divider /></div>
		<div class="mx-10">
			<span class="text-caption color-grey"> and </span>
		</div>
		<div class="w-100 pt-3 dividerMaxWidth"><v-divider /></div>
		<div class="w-100"></div>
	</div>
</template>

<style scoped lang="scss">
.dividerMaxWidth {
	max-width: 100px;
}
.color-on-surface {
	color: rgb(var(--v-theme-on-surface));
}
</style>
